import { FunctionComponent } from "react";
import { UseFormSetValue } from "react-hook-form";
import { Link } from "react-router-dom";

import { useTranslation } from "react-i18next";

import { faArrowUpRightFromSquare, faReply } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components/Icon/Icon";

import { useLazyGetContactsQuery } from "@/pages/Private/redux/contact/contact.api";

import { ProspectInterest } from "@/enum/prospect.enum";

import {
	CompanyEnrichAutoComplete,
	CompanyEnrichAutoCompleteItem,
} from "./CompanyEnrichAutoComplete";
import { CreateCompanyEnrich, MAX_LOOKALIKE_DOMAINS } from "../schema/companyEnrich";
import { normalizeDomainInput } from "../utils/normalizeDomainInput";

interface LookalikeSeedCardProps {
	formValues: Partial<CreateCompanyEnrich>;
	setValue: UseFormSetValue<CreateCompanyEnrich>;
	fetchCompanyDomains: (query: string) => Promise<CompanyEnrichAutoCompleteItem[]>;
}

const POSITIVE_REPLIES_LIMIT = 10;

export const LookalikeSeedCard: FunctionComponent<LookalikeSeedCardProps> = ({
	formValues,
	setValue,
	fetchCompanyDomains,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`companyEnrich.${key}`);

	const [triggerGetContacts, { isFetching }] = useLazyGetContactsQuery();

	const selectedDomains = formValues.companyFilterSimilar?.domains ?? [];

	const atMax = selectedDomains.length >= MAX_LOOKALIKE_DOMAINS;

	const setDomains = (domains: string[]) => {
		setValue("companyFilterSimilar.domains", domains.slice(0, MAX_LOOKALIKE_DOMAINS), {
			shouldDirty: true,
			shouldValidate: true,
		});
	};

	const handlePrefillPositiveReplies = async () => {
		try {
			const result = await triggerGetContacts({
				page: 1,
				limit: POSITIVE_REPLIES_LIMIT,
				sortBy: "prospect.lastResponseAt",
				sortOrder: "DESC",
				filter: JSON.stringify({
					bestSentiment: {
						type: "dropdown",
						filterParam: "conversation.bestSentiment",
						value: [{ id: ProspectInterest.INTERESTED, title: ProspectInterest.INTERESTED }],
					},
				}),
			}).unwrap();

			const domains = (result.data ?? [])
				.map((prospect) => prospect.organization?.primaryDomain)
				.filter((domain): domain is string => !!domain)
				.map(normalizeDomainInput);

			setDomains(Array.from(new Set([...selectedDomains, ...domains])));
		} catch (err) {
			console.error(err);
		}
	};

	return (
		<div className="p-4 rounded-xl border border-blue-action bg-brand-white">
			<div className="flex items-start justify-between gap-4 mb-1">
				<div className="text-smd text-text font-bold">{ts("lookalikeSeedTitle")}</div>
				<Link
					className="flex items-center gap-1.5 text-xs font-semibold text-blue-action hover:underline whitespace-nowrap"
					target="_blank"
					to="/app/contacts?tab=COMPANIES"
				>
					{ts("lookalikeSearchInYourCompanies")}
					<Icon className="!w-[11px] h-auto" icon={faArrowUpRightFromSquare} />
				</Link>
			</div>
			<div className="text-ssm text-text-meta mb-4">{ts("lookalikeSeedDescription")}</div>

			<CompanyEnrichAutoComplete
				allowCustomValues
				normalizeCustomValue={normalizeDomainInput}
				fetchFn={fetchCompanyDomains}
				handleSelect={(value) => setDomains(value?.map((r) => r.id) || [])}
				label={`${ts("similarDomainsLabel")} (${selectedDomains.length}/${MAX_LOOKALIKE_DOMAINS})`}
				placeholder={ts("similarDomainsPlaceholder")}
				selectedItems={selectedDomains.map((r) => ({ id: r, title: r }))}
				testId="autocomplete-similar-domains"
			/>
			{atMax && (
				<div className="text-xs text-text-meta mt-1">
					{t("companyEnrich.similarDomainsMax", { count: MAX_LOOKALIKE_DOMAINS })}
				</div>
			)}

			<div className="flex flex-wrap gap-2 mt-3">
				<button
					className="flex items-center gap-2 text-xs font-medium text-text-meta border border-border-light rounded-full px-3 py-1.5 hover:border-blue-action hover:text-blue-action disabled:opacity-50"
					disabled={isFetching || atMax}
					type="button"
					onClick={handlePrefillPositiveReplies}
				>
					<Icon className="!w-[11px] h-auto" icon={faReply} />
					{ts("lookalikePrefillPositiveReplies")}
				</button>
			</div>
		</div>
	);
};
